import { useState, type ReactNode } from 'react';
import {
  CircleCheck,
  CircleDashed,
  CircleHelp,
  Info,
  OctagonAlert,
  RefreshCw,
  ShieldQuestion,
  TriangleAlert,
} from 'lucide-react';
import { Button, Card, badgeStyles, cn, type Tone } from '@/components/ui';
import type {
  BlunderVerdict,
  FlaggedPoint,
  VerdictLimitation,
} from '@/logic/adjustments/blunderVerdict';
import { formatConfidence } from '@/logic/statistics';
import type { PointPair } from '@/types';
import { RejectionAction } from './RejectionAction';

const Chip = ({
  point,
  tone,
  onSelectPoint,
}: {
  point: FlaggedPoint;
  tone: 'danger' | 'warning';
  onSelectPoint: (id: number) => void;
}) => (
  <button
    type="button"
    aria-label={`Show flagged pair ${point.label}`}
    onClick={() => onSelectPoint(point.id)}
    className={badgeStyles({
      tone,
      size: 'md',
      interactive: true,
      className: 'font-mono',
    })}
  >
    <span
      aria-hidden="true"
      className={cn(
        'size-1.5 rounded-full',
        tone === 'danger' ? 'bg-danger' : 'bg-warning'
      )}
    />
    {point.label}
  </button>
);

const CHIP_LIMIT = 6;

/**
 * Flagged pairs as badges that select the pair in the table and map. Pairs
 * in `agreed` (flagged by both methods) use the danger tone and come first;
 * long lists collapse behind a "more" toggle.
 */
export const FlaggedPairChips = ({
  points,
  agreed,
  onSelectPoint,
}: {
  points: readonly FlaggedPoint[];
  agreed: ReadonlySet<number>;
  onSelectPoint: (id: number) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const ordered = [
    ...points.filter((point) => agreed.has(point.id)),
    ...points.filter((point) => !agreed.has(point.id)),
  ];
  const shown = expanded ? ordered : ordered.slice(0, CHIP_LIMIT);
  const hidden = ordered.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {shown.map((point) => (
        <Chip
          key={point.id}
          point={point}
          tone={agreed.has(point.id) ? 'danger' : 'warning'}
          onSelectPoint={onSelectPoint}
        />
      ))}
      {(hidden > 0 || expanded) && ordered.length > CHIP_LIMIT && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className={badgeStyles({ interactive: true, size: 'md' })}
        >
          {expanded ? 'Show fewer' : `+${hidden} more`}
        </button>
      )}
    </span>
  );
};

const LIMITATION_TEXT: Record<
  VerdictLimitation,
  (state: BlunderVerdict['state']) => string
> = {
  insufficientRedundancy: () =>
    'Too little redundancy for data snooping; remaining pairs were not tested.',
  lowPower: (state) =>
    state === 'agree' || state === 'differ'
      ? 'Low power: smaller blunders may remain undetected.'
      : 'Low power: a blunder near the minimal detectable bias would likely pass.',
  outdatedResults: () =>
    'Results come from an earlier version of the methods. Re-run the check.',
};

interface MethodRow {
  label: string;
  points: FlaggedPoint[];
  agreed: Set<number>;
}

const Row = ({
  row,
  pointPairs,
  canPreview,
  emphasize,
  previewBlockedReason,
  onPreview,
  onSelectPoint,
}: {
  row: MethodRow;
  pointPairs: PointPair[];
  canPreview: boolean;
  emphasize: boolean;
  previewBlockedReason?: string;
  onPreview: (ids: number[]) => void;
  onSelectPoint: (id: number) => void;
}) => (
  <li className="space-y-2 px-3 py-2.5">
    <div className="flex min-h-8 items-center justify-between gap-3">
      <span className="min-w-0 text-xs font-medium text-fg-muted">
        {row.label}
      </span>
      {row.points.length === 0 ? (
        <span className="text-xs text-fg-muted">No pairs flagged</span>
      ) : (
        canPreview && (
          <RejectionAction
            compact
            ids={row.points.map((point) => point.id)}
            labels={row.points.map((point) => point.label)}
            pointPairs={pointPairs}
            onPreview={onPreview}
            variant={emphasize ? 'primary' : 'secondary'}
            disabledReason={previewBlockedReason}
          />
        )
      )}
    </div>
    {row.points.length > 0 && (
      <FlaggedPairChips
        points={row.points}
        agreed={row.agreed}
        onSelectPoint={onSelectPoint}
      />
    )}
  </li>
);

export interface VerdictPanelProps {
  verdict: BlunderVerdict;
  /** Retained detection results describe previous inputs. */
  detectionStale: boolean;
  hasSolution: boolean;
  /** Enough pairs in the fit for detection. */
  canCheck: boolean;
  /** Significance level of the data snooping test, for the confidence label. */
  alpha: number;
  pointPairs: PointPair[];
  /** Make this card's preview the screen's primary action. */
  emphasizePreview: boolean;
  /** Offer "Re-run check" here (when the summary is not already offering it). */
  showRecheck: boolean;
  busy: boolean;
  onRecheck: () => void;
  onPreview: (ids: number[]) => void;
  onSelectPoint: (id: number) => void;
}

const TONE_ICON: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-fg-muted',
  accent: 'bg-accent-muted text-accent',
  success: 'bg-success-muted text-success-fg',
  warning: 'bg-warning-muted text-warning-fg',
  danger: 'bg-danger-muted text-danger-fg',
};

/**
 * Cross-method blunder verdict: the decision that matters after a solve.
 * Flags come only from current detection results.
 */
export const VerdictPanel = ({
  verdict,
  detectionStale,
  hasSolution,
  canCheck,
  alpha,
  pointPairs,
  emphasizePreview,
  showRecheck,
  busy,
  onRecheck,
  onPreview,
  onSelectPoint,
}: VerdictPanelProps) => {
  const agreed = new Set(verdict.agreed.map((point) => point.id));
  const confidence = formatConfidence(alpha);
  const combinatorialLabel = 'Combinatorial search';
  const snoopingLabel = `Data snooping · ${confidence}`;

  let tone: Tone = 'neutral';
  let icon: ReactNode = <ShieldQuestion />;
  let title: string;
  let body: ReactNode = null;
  let rows: MethodRow[] = [];

  if (detectionStale) {
    tone = 'warning';
    icon = <TriangleAlert />;
    title = 'Detection is outdated';
    body =
      'Inputs or the scale setting changed after the last check; its flags no longer apply.';
  } else if (verdict.state === 'none') {
    icon = <CircleDashed />;
    title = 'Not checked yet';
    body = !hasSolution
      ? 'Solve first, then check for blunders.'
      : canCheck
        ? 'Runs the combinatorial search and data snooping, then compares their flags.'
        : 'Blunder detection needs at least three complete pairs in the fit.';
  } else if (verdict.state === 'single') {
    tone = 'accent';
    icon = <CircleHelp />;
    title = 'Partial check';
    body = `Only ${verdict.combinatorial ? 'the combinatorial search' : 'data snooping'} has run. Check for blunders to cross-check with the other method.`;
    rows = [
      verdict.combinatorial
        ? {
            label: combinatorialLabel,
            points: verdict.combinatorial,
            agreed,
          }
        : {
            label: snoopingLabel,
            points: verdict.dataSnooping ?? [],
            agreed,
          },
    ];
  } else if (verdict.state === 'clean') {
    tone = 'success';
    icon = <CircleCheck />;
    title = 'No blunders detected';
    body = `Both methods agree at ${confidence} confidence: no pair stands out from the fit.`;
  } else if (verdict.state === 'agree') {
    tone = 'danger';
    icon = <OctagonAlert />;
    title =
      verdict.agreed.length === 1
        ? 'Both methods flag 1 pair'
        : `Both methods flag ${verdict.agreed.length} pairs`;
    body =
      'Agreement is strong evidence. Confirm it against field evidence before rejecting.';
    rows = [{ label: 'Both methods', points: verdict.agreed, agreed }];
  } else if (verdict.state === 'differ') {
    tone = 'warning';
    icon = <TriangleAlert />;
    title = 'Methods disagree';
    body =
      'Inspect each flagged pair against field evidence before rejecting any.';
    rows = [
      {
        label: combinatorialLabel,
        points: verdict.combinatorial ?? [],
        agreed,
      },
      { label: snoopingLabel, points: verdict.dataSnooping ?? [], agreed },
    ];
  } else {
    tone = 'warning';
    icon = <CircleHelp />;
    title = 'Inconclusive';
    body =
      verdict.agreed.length > 0
        ? 'Both methods flag the same pairs, but a limitation weakens the evidence.'
        : 'Neither method flags a pair, but that is not evidence of clean data.';
    if (verdict.agreed.length > 0)
      rows = [{ label: 'Both methods', points: verdict.agreed, agreed }];
  }

  const limitations = detectionStale ? [] : verdict.limitations;

  return (
    <Card
      role="region"
      aria-label="Blunder verdict"
      className="flex min-w-0 flex-col gap-3 px-4 py-4 sm:px-5"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-lg [&_svg]:size-4',
            TONE_ICON[tone]
          )}
        >
          {icon}
        </span>
        <div className="min-w-0 space-y-0.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
            Blunder check
          </p>
          <h2 className="text-base font-semibold text-fg">{title}</h2>
        </div>
      </div>
      {body && <p className="text-sm text-fg-muted">{body}</p>}
      {rows.length > 0 && (
        <ul
          aria-label="Flags by method"
          className="divide-y divide-border rounded-lg border border-border bg-surface-sunken/50"
        >
          {rows.map((row) => (
            <Row
              key={row.label}
              row={row}
              pointPairs={pointPairs}
              canPreview={!detectionStale}
              emphasize={emphasizePreview && rows.length === 1}
              previewBlockedReason={
                busy ? 'A calculation is running' : undefined
              }
              onPreview={onPreview}
              onSelectPoint={onSelectPoint}
            />
          ))}
        </ul>
      )}
      {limitations.length > 0 && (
        <ul className="space-y-1.5" aria-label="Limitations">
          {limitations.map((limitation) => (
            <li
              key={limitation}
              className="flex items-start gap-2 text-xs text-fg-muted"
            >
              <Info
                aria-hidden="true"
                className="mt-px size-3.5 shrink-0 text-warning-fg"
              />
              <span>{LIMITATION_TEXT[limitation](verdict.state)}</span>
            </li>
          ))}
        </ul>
      )}
      {detectionStale && showRecheck && (
        <Button
          className="self-start"
          size="sm"
          leadingIcon={<RefreshCw />}
          loading={busy}
          onClick={onRecheck}
        >
          Re-run check
        </Button>
      )}
    </Card>
  );
};
